import React, { useState, useEffect, useCallback } from 'react';
import { getCategoryById } from '../../constants/dawsonCategories';

const PhotoViewer = ({ photo, photos, onClose, onDelete, onNavigate }) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showInfo, setShowInfo] = useState(false);

  const category = photo ? getCategoryById(photo.category) : null;

  const currentIndex = photos.findIndex(p => p.id === photo?.id);

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        if (currentIndex > 0) onNavigate(photos[currentIndex - 1]);
        break;
      case 'ArrowRight':
        if (currentIndex < photos.length - 1) onNavigate(photos[currentIndex + 1]);
        break;
      case '+':
      case '=':
        setZoom(prev => Math.min(prev + 0.25, 5));
        break;
      case '-':
        setZoom(prev => Math.max(prev - 0.25, 0.25));
        break;
      case '0':
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        break;
      default:
        break;
    }
  }, [currentIndex, photos, onClose, onNavigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [photo]);

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.25, Math.min(5, prev + delta)));
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) return date.toDate().toLocaleString();
    return new Date(date).toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  if (!photo) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex flex-col">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black bg-opacity-50">
        <div className="flex items-center space-x-4">
          <button onClick={onClose} className="text-white hover:text-gray-300">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div>
            <h3 className="text-white font-medium text-sm">
              {category?.name || photo.category}
            </h3>
            <p className="text-gray-400 text-xs">
              {currentIndex + 1} of {photos.length}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {/* Zoom controls */}
          <button
            onClick={() => setZoom(prev => Math.max(0.25, prev - 0.25))}
            className="text-white hover:text-gray-300 p-1"
            title="Zoom out"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          </button>
          <span className="text-white text-xs min-w-[3rem] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom(prev => Math.min(5, prev + 0.25))}
            className="text-white hover:text-gray-300 p-1"
            title="Zoom in"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          </button>
          <button
            onClick={() => { setZoom(1); setPosition({ x: 0, y: 0 }); }}
            className="text-white hover:text-gray-300 p-1 text-xs"
            title="Reset zoom"
          >
            Fit
          </button>
          <div className="w-px h-5 bg-gray-600 mx-2"></div>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className={`p-1 ${showInfo ? 'text-blue-400' : 'text-white hover:text-gray-300'}`}
            title="Photo info"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(photo)}
            className="text-red-400 hover:text-red-300 p-1"
            title="Delete photo"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Image Area */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Previous button */}
        {currentIndex > 0 && (
          <button
            onClick={() => onNavigate(photos[currentIndex - 1])}
            className="absolute left-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Image */}
        <div
          className="relative"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          <img
            src={photo.downloadURL}
            alt={category?.name || photo.category}
            className="max-h-[80vh] max-w-[80vw] object-contain select-none"
            style={{
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              transition: isDragging ? 'none' : 'transform 0.1s ease'
            }}
            draggable={false}
          />
        </div>

        {/* Next button */}
        {currentIndex < photos.length - 1 && (
          <button
            onClick={() => onNavigate(photos[currentIndex + 1])}
            className="absolute right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Info Panel */}
        {showInfo && (
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-gray-900 bg-opacity-95 p-4 overflow-y-auto">
            <h3 className="text-white font-medium mb-4">Photo Details</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-400">Category</dt>
                <dd className="text-sm text-white">{category?.name || photo.category}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Type</dt>
                <dd className="text-sm text-white capitalize">{category?.type || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">File Name</dt>
                <dd className="text-sm text-white break-all">{photo.filename}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">File Size</dt>
                <dd className="text-sm text-white">{formatFileSize(photo.fileSize)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">File Type</dt>
                <dd className="text-sm text-white">{photo.fileType}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-400">Uploaded</dt>
                <dd className="text-sm text-white">{formatDate(photo.uploadedAt)}</dd>
              </div>
            </dl>

            {category?.instructions && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <h4 className="text-xs text-gray-400 mb-2">Photo Guidelines</h4>
                <ul className="text-xs text-gray-300 space-y-1">
                  {category.instructions.map((inst, i) => (
                    <li key={i}>&#8226; {inst}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {photos.length > 1 && (
        <div className="bg-black bg-opacity-50 px-4 py-2 overflow-x-auto">
          <div className="flex space-x-2 justify-center">
            {photos.map((p) => (
              <button
                key={p.id}
                onClick={() => onNavigate(p)}
                className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition ${
                  p.id === photo.id ? 'border-blue-500' : 'border-transparent hover:border-gray-500'
                }`}
              >
                <img
                  src={p.downloadURL}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoViewer;
